import { useParams, useSearchParams } from "react-router-dom";
import * as RD from "src/types/remoteData";
import { ORGANIZATION } from "./constants";

type Organization = { path: string };

function template(
  entity: string,
  organization?: Organization,
  action?: string,
  params?: string
): string {
  const orgPart = organization?.path ?? ORGANIZATION;
  const actionPart = action ?? "";
  const paramsPart = params ? `?${params}` : "";

  return entity
    ? `/${orgPart}/admin/${entity}${actionPart}${paramsPart}`
    : `/${orgPart}/admin${actionPart}${paramsPart}`;
}
export const index = (organization: Organization, params?: string): string =>
  template("", organization, undefined, params);
export const indexPath = template("");

// Enrollment Period Tab Hashes
export const ENROLLMENT_PERIOD_TAB_HASH = {
  GENERAL: "general",
  SCHOOLS: "schools",
  FORMS: "forms",
  CAPACITY: "capacity",
} as const;

export type EnrollmentPeriodTabHash =
  (typeof ENROLLMENT_PERIOD_TAB_HASH)[keyof typeof ENROLLMENT_PERIOD_TAB_HASH];

export const Enrollments = {
  index: (organization: Organization): string =>
    template("enrollments", organization),
  new: (organization: Organization): string =>
    template("enrollments", organization, "/new"),
  edit: (
    organization: Organization,
    id: string,
    hash?: EnrollmentPeriodTabHash
  ): string => {
    const path = template("enrollments", organization, `/${id}/edit`);
    return hash ? `${path}#${hash}` : path;
  },
  indexPath: template("enrollments"),
  newPath: template("enrollments", undefined, "/new"),
  editPath: template("enrollments", undefined, "/:enrollmentPeriodId/edit"),
};

export const ENROLLMENT_PERIOD_FORM_TEMPLATES_PATH = "form-templates";

export const FORM_TEMPLATE_TAB_HASH = {
  CONTENT: "content",
  STATUSES: "statuses",
  AUTOMATIC_MESSAGES: "automatic-messages",
  SETTINGS: "settings",
  RULES: "rules",
} as const;

export type FormTemplateTabHash =
  (typeof FORM_TEMPLATE_TAB_HASH)[keyof typeof FORM_TEMPLATE_TAB_HASH];

export const FormTemplates = {
  view: (
    organization: Organization,
    enrollmentPeriodId: uuid,
    formTemplateId: uuid,
    hash?: FormTemplateTabHash
  ): string => {
    const path = [
      Enrollments.edit(organization, enrollmentPeriodId),
      ENROLLMENT_PERIOD_FORM_TEMPLATES_PATH,
      formTemplateId,
      "view",
    ].join("/");
    return hash ? `${path}#${hash}` : path;
  },
  edit: (
    organization: Organization,
    enrollmentPeriodId: uuid,
    formTemplateId: uuid,
    hash?: FormTemplateTabHash
  ): string => {
    const path = [
      Enrollments.edit(organization, enrollmentPeriodId),
      ENROLLMENT_PERIOD_FORM_TEMPLATES_PATH,
      formTemplateId,
    ].join("/");
    return hash ? `${path}#${hash}` : path;
  },
  new: (organization: Organization, enrollmentPeriodId: uuid): string => {
    return [
      Enrollments.edit(organization, enrollmentPeriodId),
      ENROLLMENT_PERIOD_FORM_TEMPLATES_PATH,
      "new",
    ].join("/");
  },
  editPath: [
    Enrollments.editPath,
    ENROLLMENT_PERIOD_FORM_TEMPLATES_PATH,
    ":formTemplateId",
  ].join("/"),
  viewPath: [
    Enrollments.editPath,
    ENROLLMENT_PERIOD_FORM_TEMPLATES_PATH,
    ":formTemplateId",
    "view",
  ].join("/"),
  newPath: [
    Enrollments.editPath,
    ENROLLMENT_PERIOD_FORM_TEMPLATES_PATH,
    "new",
  ].join("/"),
};

export const FormTemplateContent = {
  edit: (
    organization: Organization,
    enrollmentPeriodId: uuid,
    formTemplateId: uuid
  ): string => {
    return FormTemplates.edit(
      organization,
      enrollmentPeriodId,
      formTemplateId,
      FORM_TEMPLATE_TAB_HASH.CONTENT
    );
  },
};

export const FormTemplateStatuses = {
  edit: (
    organization: Organization,
    enrollmentPeriodId: uuid,
    formTemplateId: uuid
  ): string => {
    return FormTemplates.edit(
      organization,
      enrollmentPeriodId,
      formTemplateId,
      FORM_TEMPLATE_TAB_HASH.STATUSES
    );
  },
};

export const FormTemplateSettings = {
  edit: (
    organization: Organization,
    enrollmentPeriodId: uuid,
    formTemplateId: uuid
  ): string => {
    return FormTemplates.edit(
      organization,
      enrollmentPeriodId,
      formTemplateId,
      FORM_TEMPLATE_TAB_HASH.STATUSES
    );
  },
};

export const FormTemplateRules = {
  edit: (
    organization: Organization,
    enrollmentPeriodId: uuid,
    formTemplateId: uuid,
    params?: string
  ): string => {
    return `${[
      FormTemplates.edit(
        organization,
        enrollmentPeriodId,
        formTemplateId,
        FORM_TEMPLATE_TAB_HASH.RULES
      ),
    ].join("/")}${params ? `?${params}` : ""}`;
  },
};

export const MessageTemplate = {
  edit: (
    organization: Organization,
    enrollmentPeriodId: uuid,
    formTemplateId: string,
    messageTemplateType: string,
    schoolId: uuid | null,
    isViewOnly: boolean
  ): string => {
    const searchParams = new URLSearchParams();
    searchParams.append("mode", "edit");
    searchParams.append("messageTemplateType", messageTemplateType);
    searchParams.append("schoolId", schoolId ?? "standard");
    searchParams.append("isViewOnly", `${isViewOnly}`);
    return `${FormTemplates.edit(
      organization,
      enrollmentPeriodId,
      formTemplateId
    )}?${searchParams.toString()}#${FORM_TEMPLATE_TAB_HASH.AUTOMATIC_MESSAGES}`;
  },
  useParams: () => {
    const [searchParams] = useSearchParams();
    const params = useParams();
    return {
      mode: searchParams.get("mode"),
      messageTemplateType: searchParams.get("messageTemplateType"),
      schoolId: searchParams.get("schoolId"),
      isViewOnly: searchParams.get("isViewOnly"),
      enrollmentPeriodId: params["enrollmentPeriodId"],
      formTemplateId: params["formTemplateId"],
    };
  },
};

export const Schools = {
  index: (organization: Organization): string =>
    template("schools", organization),
  new: (organization: Organization): string =>
    template("schools", organization, "/new"),
  edit: (organization: Organization, id: string): string =>
    template("schools", organization, `/${id}/edit`),
  indexPath: template("schools"),
  newPath: template("schools", undefined, "/new"),
  editPath: template("schools", undefined, "/:id/edit"),
};

export const Students = {
  index: (organization: Organization): string =>
    template("students", organization),
  new: (organization: Organization, params?: string): string =>
    template("students", organization, "/new", params),
  edit: (
    organization: Organization | RD.RemoteData<unknown, Organization>,
    id: string
  ): string => {
    const getUrl = (org: Organization) =>
      template("students", org, `/${id}/edit`);
    return RD.isRemoteData(organization)
      ? organization.map(getUrl).withDefault("#")
      : getUrl(organization);
  },
  view: (
    organization: Organization | RD.RemoteData<unknown, Organization>,
    id: string
  ): string => {
    const getUrl = (org: Organization) =>
      template("students", org, `/${id}/view`);
    return RD.isRemoteData(organization)
      ? organization.map(getUrl).withDefault("#")
      : getUrl(organization);
  },
  indexPath: template("students"),
  newPath: template("students", undefined, "/new"),
  editPath: template("students", undefined, "/:id/edit"),
  viewPath: template("students", undefined, "/:id/view"),
};

export const Parents = {
  index: (organization: Organization): string =>
    template("parents", organization),
  new: (organization: Organization): string =>
    template("parents", organization, "/new"),
  edit: (
    organization: Organization | RD.RemoteData<unknown, Organization>,
    id: string
  ): string => {
    const getUrl = (org: Organization) =>
      template("parents", org, `/${id}/edit`);
    return RD.isRemoteData(organization)
      ? organization.map(getUrl).withDefault("#")
      : getUrl(organization);
  },
  view: (
    organization: Organization | RD.RemoteData<unknown, Organization>,
    id: string
  ): string => {
    const getUrl = (org: Organization) =>
      template("parents", org, `/${id}/view`);
    return RD.isRemoteData(organization)
      ? organization.map(getUrl).withDefault("#")
      : getUrl(organization);
  },
  indexPath: template("parents"),
  newPath: template("parents", undefined, "/new"),
  editPath: template("parents", undefined, "/:id/edit"),
  viewPath: template("parents", undefined, "/:id/view"),
};

export const Forms = {
  index: ({
    organization,
    formTemplateId,
    params,
  }: {
    organization: Organization;
    formTemplateId: uuid;
    params?: string;
  }): string => template("forms", organization, `/${formTemplateId}`, params),
  new: ({
    organization,
    formTemplateId,
    params,
  }: {
    organization: Organization;
    formTemplateId: uuid;
    params: string;
  }): string =>
    template("forms", organization, `/${formTemplateId}/new`, params),
  edit: ({
    organization,
    id,
    formTemplateId,
    params,
  }: {
    organization: Organization;
    id: string;
    formTemplateId: uuid;
    params: string;
  }): string =>
    template("forms", organization, `/${formTemplateId}/${id}/edit`, params),
  view: ({
    organization,
    formTemplateId,
    id,
    params,
  }: {
    organization: RD.RemoteData<unknown, Organization>;
    formTemplateId: string;
    id: string;
    params?: string;
  }): string =>
    organization
      .map((org: Organization) =>
        template("forms", org, `/${formTemplateId}/${id}/view`, params)
      )
      .withDefault("#"),
  verifications: ({
    organization,
    formTemplateId,
    params,
  }: {
    organization: Organization;
    formTemplateId: uuid;
    params: string;
  }): string =>
    template("forms", organization, `/${formTemplateId}/verify`, params),
  export: ({
    organization,
    formTemplateId,
    params,
  }: {
    organization: Organization;
    formTemplateId: uuid;
    params: string;
  }): string =>
    template("forms", organization, `/${formTemplateId}/export`, params),
  newMessage: ({
    organization,
    formTemplateId,
    params,
  }: {
    organization: Organization;
    formTemplateId?: string;
    params?: string;
  }): string =>
    formTemplateId
      ? template(
          "forms",
          organization,
          `/${formTemplateId}/message/new`,
          params
        )
      : Messages.new(organization, params),
  advancedViewSettings: ({
    organization,
    formTemplateId,
    params,
  }: {
    organization: Organization;
    formTemplateId: uuid;
    params: string;
  }): string =>
    template(
      "forms",
      organization,
      `/${formTemplateId}/advancedViewSettings`,
      params
    ),
  activity: ({
    organization,
    formTemplateId,
    params,
  }: {
    organization: Organization;
    formTemplateId: uuid;
    params?: string;
  }): string =>
    template("forms", organization, `/${formTemplateId}/activity`, params),
  activityDetail: ({
    organization,
    formTemplateId,
    bulkActionId,
    params,
  }: {
    organization: Organization;
    formTemplateId: uuid;
    bulkActionId: uuid;
    params?: string;
  }): string =>
    template(
      "forms",
      organization,
      `/${formTemplateId}/activity/${bulkActionId}`,
      params
    ),
  indexPath: template("forms", undefined, "/:formTemplateId"),
  newPath: template("forms", undefined, "/:formTemplateId/new"),
  editPath: template("forms", undefined, "/:formTemplateId/:id/edit"),
  viewPath: template("forms", undefined, "/:formTemplateId/:id/view"),
  newMessagePath: template("forms", undefined, "/:formTemplateId/message/new"),
  verificationsPath: template("forms", undefined, `/:formTemplateId/verify`),
  exportPath: template("forms", undefined, "/:formTemplateId/export"),
  advancedViewSettingsPath: template(
    "forms",
    undefined,
    "/:formTemplateId/advancedViewSettings"
  ),
  activityPath: template("forms", undefined, "/:formTemplateId/activity"),
  activityDetailPath: template(
    "forms",
    undefined,
    "/:formTemplateId/activity/:bulkActionId"
  ),
};

export const FormImport = {
  view: (
    organization: Organization,
    formTemplateId: uuid,
    params?: string
  ): string =>
    template("forms", organization, `/${formTemplateId}/import`, params),
  viewPath: template("forms", undefined, "/:formTemplateId/import"),
};

export const Team = {
  index: (organization: Organization): string => template("team", organization),
  newTeam: (organization: Organization): string =>
    template("team", organization, "/new"),
  editTeam: (organization: Organization, id: string): string =>
    template("team", organization, `/${id}/edit`),
  newMember: (organization: Organization): string =>
    template("team", organization, "/member/new"),
  editMember: (organization: Organization, id: string): string =>
    template("team", organization, `/member/${id}/edit`),
  indexPath: template("team"),
  newTeamPath: template("team", undefined, "/new"),
  editTeamPath: template("team", undefined, "/:id/edit"),
  newMemberPath: template("team", undefined, "/member/new"),
  editMemberPath: template("team", undefined, "/member/:id/edit"),
};

export const Payments = {
  index: (organization: Organization): string =>
    template("payments", organization),
  indexPath: template("payments"),
  activity: (organization: Organization): string =>
    template("payments", organization, "/activity"),
  activityPath: template("payments", undefined, "/activity"),
};

export const DataServices = {
  index: (organization: Organization): string =>
    template("data_services", organization),
  indexPath: template("data_services"),
};

export const ApiCredentials = {
  new: (organization: Organization): string =>
    template("api-credential", organization, "/new"),
  edit: (organization: Organization, pk: string): string =>
    template("api-credential", organization, `/${pk}/edit`),
  newPath: template("api-credential", undefined, "/new"),
  editPath: template("api-credential", undefined, "/:pk/edit"),
};

export const Webhooks = {
  new: (organization: Organization): string =>
    template("webhooks", organization, "/new"),
  edit: (organization: Organization, pk: string): string =>
    template("webhooks", organization, `/${pk}/edit`),
  newPath: template("webhooks", undefined, "/new"),
  editPath: template("webhooks", undefined, "/:pk/edit"),
};

export const SCHEDULED_EXPORTS_PATH = "scheduled-exports";
export const ScheduledExports = {
  view: (organization: Organization): string =>
    template(SCHEDULED_EXPORTS_PATH, organization, "/view"),
  viewPath: template(SCHEDULED_EXPORTS_PATH, undefined, "/view"),
};

export const Messages = {
  index: (organization: Organization): string =>
    template("messages", organization),
  new: (organization: Organization, params?: string): string =>
    template("messages", organization, "/new", params),
  view: (
    organization: RD.RemoteData<unknown, Organization>,
    id: string
  ): string =>
    organization
      .map((org: Organization) => template("messages", org, `/${id}/view`))
      .withDefault("#"),
  indexPath: template("messages", undefined),
  newPath: template("messages", undefined, "/new"),
  viewPath: template("messages", undefined, "/:id/view"),
};

export const CapacitiesConfig = {
  index: (organization: Organization): string => {
    return template("capacities", organization);
  },
  indexPath: template("capacities"),
};

export const MATCH_TEMPLATES_PATH = "match-templates";
export const MatchTemplates = {
  index: (organization: Organization): string =>
    template(MATCH_TEMPLATES_PATH, organization),
  new: (organization: Organization): string =>
    template(MATCH_TEMPLATES_PATH, organization, "/new"),
  indexPath: template(MATCH_TEMPLATES_PATH, undefined),
  newPath: template(MATCH_TEMPLATES_PATH, undefined, "/new"),
};

export const MATCH_PATH = "match";
export const Match = {
  index: (organization: Organization): string =>
    template(MATCH_PATH, organization),
  configure: (organization: Organization): string =>
    template(MATCH_PATH, organization, "/configure"),
  progress: (organization: Organization, matchId: string): string =>
    template(MATCH_PATH, organization, `/${matchId}/progress`),
  details: (organization: Organization, matchId: string): string =>
    template(MATCH_PATH, organization, `/${matchId}/details`),
  rulesEdit: (
    organization: Organization,
    schoolId: string,
    binConfigId: string
  ): string =>
    template(MATCH_PATH, organization, `/${schoolId}/rules/${binConfigId}`),
  rulesView: (
    organization: Organization,
    schoolId: string,
    binConfigId: string
  ): string =>
    template(
      MATCH_PATH,
      organization,
      `/${schoolId}/rules/${binConfigId}/view`
    ),
  rulesNew: (organization: Organization, schoolId: string): string =>
    template(MATCH_PATH, organization, `/${schoolId}/rules`),
  publicView: (organization: Organization, matchId: string): string =>
    template(MATCH_PATH, organization, `/${matchId}/public-view`),
  indexPath: template(MATCH_PATH, undefined),
  configurePath: template(MATCH_PATH, undefined, "/configure"),
  progressPath: template(MATCH_PATH, undefined, "/:matchId/progress"),
  detailsPath: template(MATCH_PATH, undefined, "/:matchId/details"),
  rulesEditPath: template(
    MATCH_PATH,
    undefined,
    "/:schoolId/rules/:binConfigId"
  ),
  rulesViewPath: template(
    MATCH_PATH,
    undefined,
    "/:schoolId/rules/:binConfigId/view"
  ),
  rulesNewPath: template(MATCH_PATH, undefined, "/:schoolId/rules"),
  publicViewPath: template(MATCH_PATH, undefined, "/:matchId/public-view"),
};

export const GlossaryPath = {
  index: (organization: Organization, pathAlias: string): string =>
    template(pathAlias, organization),
  new: (organization: Organization, pathAlias: string): string =>
    template(pathAlias, organization, "/new"),
  edit: (organization: Organization, id: string, pathAlias: string): string =>
    template(pathAlias, organization, `/${id}/edit`),
  indexPath: template(":glossaryType"),
  newPath: template(":glossaryType", undefined, "/new"),
  editPath: template(":glossaryType", undefined, "/:id/edit"),
};

export const AvelaLabs = {
  aboutAvelaLabs: (organization: Organization): string =>
    template("avela-labs", organization, "/about-avela-labs"),
  aboutAvelaLabsPath: template("avela-labs", undefined, "/about-avela-labs"),
  duplicateStudentReportScrollToId: (): string => "possibleStudentDuplicates",
};

export const UserProfile = {
  home: (organization: Organization): string =>
    template("user-profile", organization),
  homePath: template("user-profile", undefined),
  security: (organization: Organization): string =>
    template("user-profile", organization, "/security"),
  securityPath: template("user-profile", undefined, "/security"),
  notifications: (organization: Organization): string =>
    template("user-profile", organization, "/notifications"),
  notificationsPath: template("user-profile", undefined, "/notifications"),
};
export const Org = {
  boundaries: (organization: Organization, tab?: string): string =>
    template("organization", organization, "/boundaries") +
    (tab ? `#${tab}` : ""),
  boundariesPath: template("organization", undefined, "/boundaries"),
  settings: (organization: Organization, tab?: string): string =>
    template("organization", organization, "/settings") +
    (tab ? `#${tab}` : ""),
  settingsPath: template("organization", undefined, "/settings"),
  editExplorePath: template(
    "organization",
    undefined,
    "/settings/explore/:explorePath"
  ),
  editExplore: (organization: Organization, exploreId: string): string =>
    template("organization", organization, `/settings/explore/${exploreId}`),

  editPolicyPath: template("organization", undefined, "/settings/edit-policy"),
  editPolicy: (organization: Organization): string =>
    template("organization", organization, `/settings/edit-policy`),

  editLandingPagePath: template(
    "organization",
    undefined,
    "/settings/landing-page"
  ),
  editLandingPage: (organization: Organization): string =>
    template("organization", organization, `/settings/landing-page`),
};
